import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { NextPage, NextPageContext } from 'next';
import Head from 'next/head';
import NextSeo from 'next-seo';
import { Router } from 'shared-routing';
import { Property } from 'shared-types/Property';

import { useLabels } from '@common/features/Menu/useLabels';
import { getModuleStatuses, propertiesKeys } from '@features/properties';
import { getAvailableRates, getRatePageSettings, getStaticRates, ratesKeys } from '@features/rates';
import { PagePropsWithSSR } from '@features/state';
import { useTranslations } from '@lang';
import { Rates } from '@routes/Rates';
import { NormalizedStore } from '@store/entity/entity.types';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { parseDateToString } from '@utils/helpers';
import { roomsKeys } from '@common/features/rooms/queries';
import { getRoomPageSettings } from '@common/features/rooms/requests';
import { FeaturesReady, useFeatureIsOn } from '@growthbook/growthbook-react';
import { Packages } from '@routes/Packages';

type RatesPageProps = {
  language: string;
  url: string;
  property: Property;
};

export const RatesPage: NextPage<RatesPageProps> = ({ language, url, property }) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const isPackagesOn = useFeatureIsOn('packages');

  const pageName = labels?.rates || t('app.rates');

  const pageTitle = property.propertyTitle ? `${pageName} | ${property.propertyTitle}` : pageName;
  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: property.socialFallbackImage?.url || '' }],
          },
        }}
      />
      <FeaturesReady timeout={500}>
        {isPackagesOn ? (
          <Packages property={property} language={language} />
        ) : (
          <Rates propertyId={property.id} language={language} />
        )}
      </FeaturesReady>
    </>
  );
};

type RatesPageInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<RatesPageProps, 'language' | 'url' | 'property'>;

RatesPage.getInitialProps = async (
  props: RatesPageInitialProps
): Promise<PagePropsWithSSR<RatesPageProps>> => {
  const { store, property, language, url, res, query, moduleStatuses } = props;
  const { id: siteId, slug: siteSlug } = property;

  if (!moduleStatuses.roomsEnabled) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];
  const promoRateCode = typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined;

  const client = new QueryClient();
  await client.prefetchQuery({
    queryKey: ratesKeys.staticRates({ siteSlug, promoRateCode }),
    queryFn: () => getStaticRates({ siteSlug, promoRateCode }),
  });
  await client.prefetchQuery({
    queryKey: ratesKeys.availableRates({
      siteId,
      fromDate,
      toDate,
      adults,
      children,
      childAges,
      promoRateCode,
      rooms,
      isMultiRoomsEnabled: rooms.length > 1,
    }),
    queryFn: () =>
      getAvailableRates({
        siteId,
        fromDate,
        toDate,
        adults,
        children,
        childAges,
        promoRateCode,
        rooms,
        isMultiRoomsEnabled: rooms.length > 1,
      }),
  });
  await client.prefetchQuery({
    queryKey: propertiesKeys.moduleStatuses({ siteSlug }),
    queryFn: () => getModuleStatuses({ siteSlug }),
  });
  await client.prefetchQuery({
    queryKey: roomsKeys.roomSettings({ siteId, language }),
    queryFn: () => getRoomPageSettings({ siteId, language }),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  await client.prefetchQuery({
    queryKey: ratesKeys.rateSettings({ siteId, language }),
    queryFn: () => getRatePageSettings({ siteId, language }),
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return {
    property,
    language,
    url,
    dehydratedState: dehydrate(client),
  };
};

export default RatesPage;
